import ModalLayout from "@/components/layout/ModalLayout";
import { Button } from "@/components/ui/Button";
import DateOneBox from "@/components/ui/DateOneBox";
import { InputBox } from "@/components/ui/InputBox";
import ModalHeader from "@/components/ui/ModalHeader";
import SelectBox from "@/components/ui/SelectBox";
import TextAreaBox from "@/components/ui/TextAreaBox";
import ToggleButton from "@/components/ui/ToggleButton";
import { CheckBox } from "@/components/ui/CheckBox";
import { useState, useEffect, useMemo } from "react";
import { OrderSlipReportRow, OrderSlip } from "@/interface/dispatch/dp_order_slip";
import { useCodeListByCompanyCode, useCodeListByCargoForm, useCodeListByCargoUnit } from "@/hooks";
import { useInsertAndUpdateOrderSlip } from "@/hooks/dispatch/dp_ordeer_slip/useOrderSlip";
import { useUserInfo } from "@/hooks/member/useMember";
import { toast } from "sonner";
import CompanySearchModal from "@/components/modal/performance/CompanySearchModal";
import { StCompanyCode } from "@/interface/standard/st_company_code";
import CarNumberSearchModal from "@/components/modal/performance/CarNumberSearchModal";
import DriverSearchModal from "@/components/modal/performance/DriverSearchModal";
import EmployeeSearchModal from "@/components/modal/performance/EmployeeSearchModal";
import PriceCodeSearchModal from "@/components/modal/performance/PriceCodeSearchModal";
import ItemCodeSearchModal from "@/components/modal/performance/ItemCodeSearchModal";
import { VehicleCode } from "@/interface/standard/st_vehicle_code";
import { DriverCode } from "@/interface/standard/st_driver_code";
import { EmployeeCode } from "@/interface/standard/st_employee_code";
import { ItemCode } from "@/interface/standard/st_item_code";
import { formatNumber } from "@/lib/utils";
import { usePricePercentage } from "@/hooks/standard/st_price_code/usePriceCode";
import { getToday, getLocalISOString } from "@/lib/date";

interface AddProps {
  isOpen: boolean;
  mode?: "add" | "edit";
  initialData?: OrderSlipReportRow | null;
  onClose: () => void;
  onSave: (data: any) => void;
}

export default function Add({ isOpen, mode = "add", initialData, onClose, onSave }: AddProps) {
  // React Query 훅으로 데이터 가져오기
  const { data: companyCodeList = [] } = useCodeListByCompanyCode();
  const { data: cargoFormList = [] } = useCodeListByCargoForm();
  const { data: cargoUnitList = [] } = useCodeListByCargoUnit();
  const { data: userInfo } = useUserInfo();

  // Mutation 훅
  const insertAndUpdateMutation = useInsertAndUpdateOrderSlip();

  const [isChecked, setIsChecked] = useState(false);
  const [companyName, setCompanyName] = useState("");
  // 오늘 날짜를 기본값으로 설정
  const today = getToday();
  const [upDate, setUpDate] = useState(today); // 상차일자 기본값: 오늘
  const [downDate, setDownDate] = useState(today); // 도착일자 기본값: 오늘
  const [dispatchDate, setDispatchDate] = useState("");
  const [month, setMonth] = useState("00"); // 시 기본값: 00시
  const [day, setDay] = useState("00"); // 분 기본값: 00분
  const [dispatchEmployee, setDispatchEmployee] = useState(""); // 배차사원명 (화면 표시용)
  const [dispatchEmployeeCode, setDispatchEmployeeCode] = useState(""); // 배차사원 코드 (DB용)
  const [vehicleNumber, setVehicleNumber] = useState(""); // 차량번호 (화면 표시용)
  const [vehicleCode, setVehicleCode] = useState(""); // 차량 코드 (DB용)
  const [driverName, setDriverName] = useState(""); // 기사명 (화면 표시용)
  const [driverCode, setDriverCode] = useState(""); // 기사 코드 (DB용)
  const [driverTel, setDriverTel] = useState("");
  const [isToggleOn, setIsToggleOn] = useState(false);
  const [remark, setRemark] = useState("");
  const [orderLocation, setOrderLocation] = useState(""); // 매출처 이름
  const [orderLocationCode, setOrderLocationCode] = useState(""); // 매출처 코드
  const [orderItem, setOrderItem] = useState(""); // 품목명
  const [orderItemCode, setOrderItemCode] = useState(""); // 품목 코드
  const [orderQuantity, setOrderQuantity] = useState("");
  const [orderType, setOrderType] = useState("");
  const [orderUnit, setOrderUnit] = useState("");
  const [appExposeYn, setAppExposeYn] = useState(true); // APP 노출 (기본값: true)

  // 검색 모달 상태
  const [isCompanySearchOpen, setIsCompanySearchOpen] = useState(false); // 매출처 검색 모달
  const [isCarNumberSearchOpen, setIsCarNumberSearchOpen] = useState(false); // 차량번호 검색 모달
  const [isDriverSearchOpen, setIsDriverSearchOpen] = useState(false); // 기사명 검색 모달
  const [isEmployeeSearchOpen, setIsEmployeeSearchOpen] = useState(false); // 배차사원 검색 모달
  const [isPriceCodeSearchOpen, setIsPriceCodeSearchOpen] = useState(false); // 단가코드 검색 모달
  const [isItemCodeSearchOpen, setIsItemCodeSearchOpen] = useState(false); // 품목 검색 모달

  // 상차정보 동적 관리
  const [loadingInfos, setLoadingInfos] = useState([{ id: 1, loadPlace: "", unloadPlace: "", quantity: "" }]);

  // 하차정보 동적 관리
  const [unloadingInfos, setUnloadingInfos] = useState([{ id: 1, unloadPlace: "", region: "", quantity: "" }]);

  // 상차 수량 (주문수량과 연동)
  const [loadingQty, setLoadingQty] = useState("");

  // 단가 정보 state
  const [saleUnitPrice, setSaleUnitPrice] = useState<number | null>(null); // 매출단가
  const [selectedPriceInfo, setSelectedPriceInfo] = useState<{
    priceCodeId: number;
    companyName: string;
    region: string;
    dist: string;
    weight: number;
  } | null>(null); // 선택된 단가코드 정보

  // 퍼센테이지 state (수정 가능)
  const [pricePercentageValue, setPricePercentageValue] = useState<number>(0);

  // 회사별 저장된 퍼센테이지 조회 (단가코드 선택 시 자동 불러오기용)
  const { data: defaultPricePercentage } = usePricePercentage(selectedPriceInfo?.priceCodeId, {
    enabled: !!selectedPriceInfo?.priceCodeId,
  });

  // 단가코드 선택 시 기본 퍼센테이지 설정 (단, 이미 저장된 값이 없을 때만)
  useEffect(() => {
    if (defaultPricePercentage !== undefined && defaultPricePercentage !== null && pricePercentageValue === null) {
      setPricePercentageValue(defaultPricePercentage);
    }
  }, [defaultPricePercentage]);

  // 모달이 열릴 때 initialData로 폼 필드 채우기
  useEffect(() => {
    // 모달이 닫혀있으면 실행하지 않음
    if (!isOpen) {
      return;
    }

    // Edit 모드: initialData가 있을 때만 실행
    if (mode === "edit" && initialData) {
      // 기본 정보
      setCompanyName(initialData.companyCode || "");
      setUpDate(initialData.orderDate || "");
      setDownDate(initialData.arrivalTime ? initialData.arrivalTime.split("T")[0] : "");

      // 시/분 설정 (백엔드에서 Integer로 제공)@
      setMonth(
        initialData.orderSlipTimeHour !== undefined && initialData.orderSlipTimeHour !== null
          ? String(initialData.orderSlipTimeHour).padStart(2, "0")
          : "00"
      );
      setDay(
        initialData.orderSlipTimeMin !== undefined && initialData.orderSlipTimeMin !== null
          ? String(initialData.orderSlipTimeMin).padStart(2, "0")
          : "00"
      );

      setRemark(initialData.remark || "");

      // 주문 정보
      setOrderLocation(initialData.custName || "");
      setOrderLocationCode(initialData.custCode || ""); // 거래처 코드 설정
      setOrderItem(initialData.itemName || "");
      setOrderItemCode(initialData.itemCode || ""); // 품목 코드 설정
      setOrderQuantity(initialData.orderQty?.toString() || "");
      setLoadingQty(initialData.inQty?.toString() || initialData.orderQty?.toString() || "");
      setOrderType(initialData.shapeCode || "");
      setOrderUnit(initialData.unitCode || "");

      // APP 노출 설정
      setAppExposeYn(initialData.appExposeYn === "Y" || initialData.appExposeYn === undefined); // 기본값: true

      // 배차 정보
      setIsToggleOn(!!initialData.dispatchStaffName || !!initialData.carNumber || !!initialData.driverName);
      setDispatchEmployee(initialData.dispatchStaffName || "");
      setDispatchEmployeeCode(initialData.dispatchStaffCode || ""); // 배차사원 코드 설정
      // 차량번호와 코드 (edit 모드에서 유지)
      const newVehicleNumber = initialData.carNumber || "";
      const newVehicleCode = initialData.carCode || "";
      const newDriverName = initialData.driverName || "";
      const newDriverCode = initialData.driverCode || "";
      const newDriverTel = initialData.driverTel || "";

      setVehicleNumber(newVehicleNumber);
      setVehicleCode(newVehicleCode); // 차량 코드 설정
      // 기사명, 코드, 전화번호 (edit 모드에서 유지)
      setDriverName(newDriverName);
      setDriverCode(newDriverCode); // 기사 코드 설정
      setDriverTel(newDriverTel);

      // 상차정보 설정
      const loadingInfosData = [];
      if (initialData.inBranch || initialData.inLocation || initialData.inQty) {
        loadingInfosData.push({
          id: 1,
          loadPlace: initialData.inBranch || "", // 대리점
          unloadPlace: initialData.inLocation || "", // 상차지
          quantity: initialData.inQty?.toString() || "",
        });
      }
      if (loadingInfosData.length === 0) {
        loadingInfosData.push({ id: 1, loadPlace: "", unloadPlace: "", quantity: "" });
      }
      setLoadingInfos(loadingInfosData);

      // 하차정보 설정
      const unloadingInfosData = [];
      if (initialData.outLocation1 || initialData.outArea1 || initialData.outQty1) {
        unloadingInfosData.push({
          id: 1,
          unloadPlace: initialData.outLocation1 || "",
          region: initialData.outArea1 || "",
          quantity: initialData.outQty1?.toString() || "",
        });
      }
      if (initialData.outLocation2 || initialData.outArea2 || initialData.outQty2) {
        unloadingInfosData.push({
          id: 2,
          unloadPlace: initialData.outLocation2 || "", // 하차지 2
          region: initialData.outArea2 || "",
          quantity: initialData.outQty2?.toString() || "",
        });
      }
      if (initialData.outLocation3 || initialData.outArea3 || initialData.outQty3) {
        unloadingInfosData.push({
          id: 3,
          unloadPlace: initialData.outLocation3 || "", // 하차지 3
          region: initialData.outArea3 || "",
          quantity: initialData.outQty3?.toString() || "",
        });
      }
      if (unloadingInfosData.length === 0) {
        unloadingInfosData.push({ id: 1, unloadPlace: "", region: "", quantity: "" });
      }
      setUnloadingInfos(unloadingInfosData);

      // 단가 정보 설정
      setSaleUnitPrice(initialData.salePrice || null);
      setPricePercentageValue(initialData.pricePercentage ?? 0);
      if (initialData.priceCodeId) {
        setSelectedPriceInfo({
          priceCodeId: initialData.priceCodeId,
          companyName: "",
          region: initialData.priceRegion || "",
          dist: initialData.priceDist || "",
          weight: initialData.priceWeight || 0,
        });
      } else {
        setSelectedPriceInfo(null);
      }

      // Edit 모드에서는 여기서 종료 (Add 모드 초기화 코드 실행 방지)
      return;
    }

    // Add 모드: initialData가 있으면 복사 모드, 없으면 일반 추가 모드
    if (mode === "add") {
      if (initialData) {
        // 주문복사 모드: initialData에서 데이터 복사하되, 주문일자는 오늘로, 상차지/하차지는 비움
        const today = getToday();
        setCompanyName(initialData.companyCode || "");
        setUpDate(today); // 오늘 날짜로 설정
        setDownDate(today); // 오늘 날짜로 설정
        setDispatchDate("");
        setMonth("00"); // 00시로 초기화
        setDay("00"); // 00분으로 초기화

        setRemark(initialData.remark || "");

        // 주문 정보
        setOrderLocation(initialData.custName || "");
        setOrderLocationCode(initialData.custCode || "");
        setOrderItem(initialData.itemName || "");
        setOrderItemCode(initialData.itemCode || "");
        setOrderQuantity(initialData.orderQty?.toString() || "");
        setOrderType(initialData.shapeCode || "");
        setOrderUnit(initialData.unitCode || "");

        // APP 노출 설정
        setAppExposeYn(initialData.appExposeYn === "Y" || initialData.appExposeYn === undefined);

        // 배차 정보
        setIsToggleOn(!!initialData.dispatchStaffName || !!initialData.carNumber || !!initialData.driverName);
        setDispatchEmployee(initialData.dispatchStaffName || "");
        setDispatchEmployeeCode(initialData.dispatchStaffCode || "");
        setVehicleNumber(initialData.carNumber || "");
        setVehicleCode(initialData.carCode || "");
        setDriverName(initialData.driverName || "");
        setDriverCode(initialData.driverCode || "");
        setDriverTel(initialData.driverTel || "");

        // 상차정보 비우기 (주문복사 시)
        setLoadingInfos([{ id: 1, loadPlace: "", unloadPlace: "", quantity: "" }]);

        // 하차정보 비우기 (주문복사 시)
        setUnloadingInfos([{ id: 1, unloadPlace: "", region: "", quantity: "" }]);

        // 단가 정보 복사 (주문복사 시)
        setSaleUnitPrice(initialData.salePrice || null);
        setPricePercentageValue(initialData.pricePercentage ?? 0);
        if (initialData.priceCodeId) {
          setSelectedPriceInfo({
            priceCodeId: initialData.priceCodeId,
            companyName: "",
            region: initialData.priceRegion || "",
            dist: initialData.priceDist || "",
            weight: initialData.priceWeight || 0,
          });
        } else {
          setSelectedPriceInfo(null);
        }
      } else {
        // 일반 추가 모드: 모든 필드 초기화
        const today = getToday();
        setCompanyName("");
        setUpDate(today); // 오늘 날짜로 초기화
        setDownDate(today); // 오늘 날짜로 초기화
        setDispatchDate("");
        setMonth("00"); // 00시로 초기화
        setDay("00"); // 00분으로 초기화
        setDispatchEmployee(userInfo?.name || ""); // 현재 로그인 사용자
        setDispatchEmployeeCode(userInfo?.personCode || ""); // 현재 로그인 사용자 코드
        setVehicleNumber("");
        setVehicleCode("");
        setDriverName("");
        setDriverCode("");
        setDriverTel("");
        setIsToggleOn(false);
        setRemark("");
        setOrderLocation("");
        setOrderLocationCode("");
        setOrderItem("");
        setOrderItemCode("");
        setOrderQuantity("");
        setLoadingQty("");
        setOrderType("");
        setOrderUnit("");
        setAppExposeYn(true); // APP 노출 기본값: true
        setLoadingInfos([{ id: 1, loadPlace: "", unloadPlace: "", quantity: "" }]);
        setUnloadingInfos([{ id: 1, unloadPlace: "", region: "", quantity: "" }]);
        setSaleUnitPrice(null); // 매출단가 초기화
        setPricePercentageValue(0); // 퍼센테이지 초기화
        setSelectedPriceInfo(null); // 단가코드 정보 초기화
      }
    }
    // Edit 모드가 아니고 Add 모드도 아니면 아무것도 하지 않음
  }, [isOpen, mode, initialData]);

  // API 데이터를 SelectBox 옵션으로 변환
  const companyOptions = useMemo(() => {
    return companyCodeList.map((item: any) => ({
      value: item.code || item.codeId?.toString() || "",
      label: item.name || "",
    }));
  }, [companyCodeList]);

  const orderTypeOptions = useMemo(() => {
    return cargoFormList.map((item: any) => ({
      value: item.code || item.codeId?.toString() || "",
      label: item.name || "",
    }));
  }, [cargoFormList]);

  const orderUnitOptions = useMemo(() => {
    return cargoUnitList.map((item: any) => ({
      value: item.code || item.codeId?.toString() || "",
      label: item.name || "",
    }));
  }, [cargoUnitList]);

  // 시 옵션 (0시 ~ 23시)
  const monthOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: `${i}시`,
  }));

  // 분 옵션 (0분 ~ 59분)
  const dayOptions = Array.from({ length: 60 }, (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: `${i}분`,
  }));

  // 하차정보 추가 함수
  const addUnloadingInfo = () => {
    if (unloadingInfos.length < 3) {
      const newId = Math.max(...unloadingInfos.map(info => info.id)) + 1;
      setUnloadingInfos([...unloadingInfos, { id: newId, unloadPlace: "", region: "", quantity: "" }]);
    }
  };

  // 상차정보 업데이트 함수
  const updateLoadingInfo = (id: number, field: string, value: string) => {
    setLoadingInfos(prev => prev.map(info => (info.id === id ? { ...info, [field]: value } : info)));
  };

  // 하차정보 업데이트 함수
  const updateUnloadingInfo = (id: number, field: string, value: string) => {
    setUnloadingInfos(prev => prev.map(info => (info.id === id ? { ...info, [field]: value } : info)));
  };

  // 하차정보 삭제 함수
  const removeUnloadingInfo = (id: number) => {
    if (unloadingInfos.length > 1) {
      setUnloadingInfos(prev => prev.filter(info => info.id !== id));
    }
  };

  // 매출처 검색 - 실제 API 사용
  const openCompanySearch = () => {
    setIsCompanySearchOpen(true);
  };

  // 품목 검색 - 실제 API 사용
  const openItemSearch = () => {
    setIsItemCodeSearchOpen(true);
  };

  // 저장 처리 함수
  const handleSave = (isContinuous: boolean = false) => {
    // 필수 필드 검증
    if (!companyName) {
      toast.error("회사명을 선택해주세요.");
      return;
    }
    if (!orderLocation) {
      toast.error("매출처를 입력해주세요.");
      return;
    }
    if (!orderItem) {
      toast.error("품목명을 입력해주세요.");
      return;
    }
    if (!orderQuantity) {
      toast.error("주문수량을 입력해주세요.");
      return;
    }
    if (!orderType) {
      toast.error("화물형태를 선택해주세요.");
      return;
    }
    if (!orderUnit) {
      toast.error("화물단위를 선택해주세요.");
      return;
    }
    if (loadingInfos.length > 0 && !loadingInfos[0].unloadPlace) {
      toast.error("상차지를 입력해주세요.");
      return;
    }
    if (unloadingInfos.length > 0 && !unloadingInfos[0].unloadPlace) {
      toast.error("하차지 1을 입력해주세요.");
      return;
    }

    // 현재 날짜/시간
    const now = getLocalISOString();
    const currentUserId = userInfo?.name || "";

    // OrderSlip 객체 생성
    const orderSlip: OrderSlip = {
      // Edit 모드일 때만 orderSlipCode 포함 (있으면 수정, 없으면 추가)
      // 빈 문자열도 제외하기 위해 trim() 추가
      orderSlipCode: mode === "edit" && initialData?.orderSlipCode?.trim() ? initialData.orderSlipCode : undefined,

      // 기본 정보
      orderSlipCompanyCode: companyName,
      orderSlipDate1: upDate ? `${upDate}T00:00:00` : undefined, // 상차일자
      orderSlipDate2: downDate ? `${downDate}T${month || "00"}:${day || "00"}:00` : undefined, // 하차일자 + 시:분
      orderSlipTimeHour: month ? parseInt(month) : undefined, // 시
      orderSlipTimeMin: day ? parseInt(day) : undefined, // 분
      orderSlipRemark: remark || undefined,

      // 주문 정보
      orderSlipCustCode: orderLocationCode || undefined, // 매출처 코드
      orderSlipItemCode: orderItemCode || undefined, // 품목 코드
      orderSlipQty1: orderQuantity ? parseFloat(orderQuantity) : undefined, // 주문수량
      orderSlipShapeCode: orderType,
      orderSlipUnitCode: orderUnit,

      // 배차 정보
      // Edit 모드: 기존 값이 있으면 항상 전송 (Toggle 상태와 관계없이 유지)
      // Add 모드: Toggle이 ON일 때만 전송
      ...(mode === "edit"
        ? (() => {
            const carCodeValue = vehicleCode?.trim() || initialData?.carCode || undefined;
            const driverCodeValue = driverCode?.trim() || initialData?.driverCode || undefined;
            const personCodeValue = dispatchEmployeeCode?.trim() || initialData?.dispatchStaffCode || undefined;

            return {
              // Edit 모드: 현재 입력된 값이 있으면 사용, 없으면 기존 값 유지
              orderSlipDateCarSet: dispatchDate ? `${dispatchDate}T00:00:00` : undefined,
              orderSlipPersonCode: personCodeValue,
              orderSlipCarCode: carCodeValue,
              orderSlipDriverCode: driverCodeValue,
            };
          })()
        : isToggleOn
          ? {
              // Add 모드: Toggle이 ON일 때만 전송
              orderSlipDateCarSet: dispatchDate ? `${dispatchDate}T00:00:00` : undefined,
              orderSlipPersonCode: dispatchEmployeeCode?.trim() || undefined,
              orderSlipCarCode: vehicleCode?.trim() || undefined,
              orderSlipDriverCode: driverCode?.trim() || undefined,
            }
          : {}),

      // 상차정보
      orderSlipInBranch: loadingInfos[0]?.loadPlace || undefined, // 상차 대리점 1
      orderSlipInLocation: loadingInfos[0]?.unloadPlace || undefined, // 상차지 1
      // orderSlipQty1은 이미 주문수량으로 사용됨

      // 하차정보
      // 하차지 1: 하차정보의 하차지 또는 상차정보 1의 하차지
      orderSlipOutLocation: unloadingInfos[0]?.unloadPlace || loadingInfos[0]?.unloadPlace || undefined,
      orderSlipOutArea1: unloadingInfos[0]?.region || undefined,
      orderSlipOutQty1: unloadingInfos[0]?.quantity ? parseFloat(unloadingInfos[0].quantity) : undefined,
      // 하차지 2, 3: 하차정보의 하차지와 지역, 수량 저장
      orderSlipOutLocation2: unloadingInfos[1]?.unloadPlace || undefined, // 하차지 2
      orderSlipOutArea2: unloadingInfos[1]?.region || undefined,
      orderSlipOutQty2: unloadingInfos[1]?.quantity ? parseFloat(unloadingInfos[1].quantity) : undefined,
      orderSlipOutLocation3: unloadingInfos[2]?.unloadPlace || undefined, // 하차지 3
      orderSlipOutArea3: unloadingInfos[2]?.region || undefined,
      orderSlipOutQty3: unloadingInfos[2]?.quantity ? parseFloat(unloadingInfos[2].quantity) : undefined,

      // 작성자 정보
      // Add 모드: writeDate1, writeId1에 현재 값 설정
      // Edit 모드: writeDate1, writeId1은 기존 값 유지, writeDate2, writeId2만 업데이트
      ...(mode === "add"
        ? {
            orderSlipWriteDate1: now,
            orderSlipWriteId1: currentUserId,
            orderSlipWriteDate2: now,
            orderSlipWriteId2: currentUserId,
          }
        : {
            // 기존 날짜 형식을 ISO 형식으로 변환 (공백 -> T)
            orderSlipWriteDate1: initialData?.orderSlipWriteDate1?.replace(" ", "T") || now,
            orderSlipWriteId1: initialData?.orderSlipWriteId1 || currentUserId,
            orderSlipWriteDate2: now, // 수정 시 date2만 업데이트
            orderSlipWriteId2: currentUserId, // 수정 시 id2만 업데이트
          }),

      // APP 노출
      appExposeYn: appExposeYn ? "Y" : "N",

      // 삭제 여부 (Edit 모드에서는 기존 값 유지, Add 모드에서는 "N")
      delYn: mode === "edit" ? initialData?.delYn || "N" : "N",

      // 단가 정보
      orderSlipSalePrice: saleUnitPrice || undefined,
      orderSlipPriceCodeId: selectedPriceInfo?.priceCodeId || undefined,
      orderSlipPriceRegion: selectedPriceInfo?.region || undefined,
      orderSlipPriceDist: selectedPriceInfo?.dist || undefined,
      orderSlipPriceWeight: selectedPriceInfo?.weight || undefined,
      orderSlipPricePercentage: pricePercentageValue ?? undefined,
    };

    // Mutation 실행
    insertAndUpdateMutation.mutate(orderSlip, {
      onSuccess: () => {
        toast.success(mode === "edit" ? "수정되었습니다." : "저장되었습니다.");

        if (isContinuous) {
          // 연속 저장: 폼 초기화
          const today = getToday();
          setCompanyName("");
          setUpDate(today); // 오늘 날짜로 초기화
          setDownDate(today); // 오늘 날짜로 초기화
          setDispatchDate("");
          setMonth("00"); // 00시로 초기화
          setDay("00"); // 00분으로 초기화
          setDispatchEmployee("");
          setDispatchEmployeeCode("");
          setVehicleNumber("");
          setVehicleCode("");
          setDriverName("");
          setDriverCode("");
          setDriverTel("");
          setIsToggleOn(false);
          setRemark("");
          setOrderLocation("");
          setOrderLocationCode("");
          setOrderItem("");
          setOrderItemCode("");
          setOrderQuantity("");
          setLoadingQty("");
          setOrderType("");
          setOrderUnit("");
          setAppExposeYn(true); // APP 노출 기본값: true
          setLoadingInfos([{ id: 1, loadPlace: "", unloadPlace: "", quantity: "" }]);
          setUnloadingInfos([{ id: 1, unloadPlace: "", region: "", quantity: "" }]);
          setSaleUnitPrice(null); // 매출단가 초기화
          setSelectedPriceInfo(null); // 단가코드 정보 초기화
        } else {
          // 일반 저장: 모달 닫기
          onSave(orderSlip);
          onClose();
        }
      },
      onError: (error: any) => {
        console.error("저장 실패:", error);
        toast.error(error?.response?.data?.message || "저장에 실패했습니다.");
      },
    });
  };

  // 날짜 포맷팅 함수
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr.replace(" ", "T"));
    if (isNaN(date.getTime())) return "-";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  // 작성자 정보 컴포넌트
  const writeInfoComponent =
    mode === "edit" && initialData ? (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>
          최초: {initialData.orderSlipWriteId1 || "-"} ({formatDateTime(initialData.orderSlipWriteDate1)})
        </span>
        <span className="text-gray-300">|</span>
        <span>
          최종: {initialData.orderSlipWriteId2 || "-"} ({formatDateTime(initialData.orderSlipWriteDate2)})
        </span>
      </div>
    ) : null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "add" ? "주문일보 추가" : "주문일보 수정"}
      maxWidth="max-w-full"
      headerRight={writeInfoComponent}
    >
      <div className="flex gap-6 h-full">
        {/* 왼쪽 컴포넌트 */}
        <div className="flex-1 min-w-0">
          <ModalHeader title="기본 정보" />
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="space-y-4">
              <SelectBox
                required
                label="회사명"
                value={companyName}
                onChange={e => setCompanyName(e)}
                options={companyOptions}
              />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="space-y-4">
              <DateOneBox label="상차일자" date={upDate} onDateChange={e => setUpDate(e)} />
            </div>
            <div className="space-y-4">
              <DateOneBox label="도착일자" date={downDate} onDateChange={e => setDownDate(e)} />
            </div>
            <div className="space-y-4">
              <SelectBox label="시" value={month} onChange={e => setMonth(e)} options={monthOptions} />
            </div>
            <div className="space-y-4">
              <SelectBox label="분" value={day} onChange={e => setDay(e)} options={dayOptions} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="space-y-4">
              <TextAreaBox label="비고" value={remark} onChange={e => setRemark(e.target.value)} />
            </div>
          </div>
          <ModalHeader
            title="주문 정보"
            rightComponent={
              <div className="flex items-center gap-4">
                <CheckBox label="APP 노출" checked={appExposeYn} onChange={e => setAppExposeYn(e.target.checked)} />
              </div>
            }
          />
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="space-y-4">
              <InputBox
                required
                label="매출처"
                value={orderLocation}
                showIcon
                onIconClick={openCompanySearch}
                readOnly
                onClick={openCompanySearch}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="space-y-4">
                <InputBox
                  required
                  label="품목명"
                  showIcon
                  onIconClick={openItemSearch}
                  value={orderItem}
                  readOnly
                  onClick={openItemSearch}
                />
              </div>
              <div className="space-y-4">
                <InputBox
                  required
                  label="주문수량"
                  value={orderQuantity}
                  onChange={e => {
                    setOrderQuantity(e.target.value);
                    setLoadingQty(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="space-y-4">
                <SelectBox
                  required
                  label="화물형태"
                  value={orderType}
                  onChange={e => setOrderType(e)}
                  options={orderTypeOptions}
                />
              </div>
              <div className="space-y-4">
                <SelectBox
                  required
                  label="화물단위"
                  value={orderUnit}
                  onChange={e => setOrderUnit(e)}
                  options={orderUnitOptions}
                />
              </div>
            </div>
            {/* 단가 정보 표시 */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <InputBox
                    label="매출단가"
                    value={saleUnitPrice ? formatNumber(saleUnitPrice) : ""}
                    readOnly
                    placeholder="단가코드를 선택하세요"
                    showIcon
                    onIconClick={() => setIsPriceCodeSearchOpen(true)}
                  />
                </div>
                <div className="flex items-end gap-1">
                  <InputBox
                    label="매입%"
                    type="number"
                    className="w-20"
                    value={pricePercentageValue ?? 0}
                    onChange={e => {
                      const value = e.target.value;
                      setPricePercentageValue(value ? parseFloat(value) : 0);
                    }}
                    placeholder="0"
                  />
                  <span className="text-sm text-gray-600 pb-3">%</span>
                </div>
              </div>
            </div>
            {/* 단가코드 정보 표시 (선택된 경우) */}
            {selectedPriceInfo && selectedPriceInfo.companyName && (
              <div className="text-xs text-gray-500 mb-4 p-2 bg-blue-50 rounded border border-blue-200">
                <span className="font-medium text-blue-700">선택된 단가:</span> {selectedPriceInfo.companyName}
                {(selectedPriceInfo.region || selectedPriceInfo.dist || selectedPriceInfo.weight > 0) && (
                  <>
                    {selectedPriceInfo.region && (
                      <>
                        <span className="font-medium ml-2">|</span>
                        <span className="font-medium ml-2">지역:</span> {selectedPriceInfo.region}
                      </>
                    )}
                    {selectedPriceInfo.dist && (
                      <>
                        <span className="font-medium ml-2">|</span>
                        <span className="font-medium ml-2">거리:</span> {selectedPriceInfo.dist}
                      </>
                    )}
                    {selectedPriceInfo.weight > 0 && (
                      <>
                        <span className="font-medium ml-2">|</span>
                        <span className="font-medium ml-2">중량:</span> {selectedPriceInfo.weight}t
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 구분선 */}
        <div className="w-4 bg-[#FFF]" />

        {/* 오른쪽 컴포넌트 */}
        <div className="flex-1 min-w-0">
          <ModalHeader
            title="배차 정보"
            rightComponent={<ToggleButton label="" checked={isToggleOn} onChange={e => setIsToggleOn(e)} />}
          />
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="space-y-4">
              <DateOneBox
                disabled={!isToggleOn}
                label="배차일자"
                date={dispatchDate}
                onDateChange={e => setDispatchDate(e)}
              />
            </div>
            <div className="space-y-4">
              <InputBox
                disabled={!isToggleOn}
                label="배차사원"
                value={dispatchEmployee}
                readOnly
                showIcon={true}
                onIconClick={() => isToggleOn && setIsEmployeeSearchOpen(true)}
                placeholder="검색하여 선택"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="space-y-4">
              <InputBox
                disabled={!isToggleOn}
                label="차량 번호"
                value={vehicleNumber}
                readOnly
                showIcon={true}
                onIconClick={() => isToggleOn && setIsCarNumberSearchOpen(true)}
                placeholder="검색하여 선택"
              />
            </div>
            <div className="space-y-4">
              <InputBox
                disabled={!isToggleOn}
                label="기사명"
                value={driverName}
                readOnly
                showIcon={true}
                onIconClick={() => isToggleOn && setIsDriverSearchOpen(true)}
                placeholder="검색하여 선택"
              />
            </div>
            <div className="space-y-4">
              <InputBox
                disabled={!isToggleOn}
                label="휴대 전화"
                value={driverTel}
                readOnly
                placeholder="기사 선택 시 자동 입력"
              />
            </div>
          </div>
          <ModalHeader title="상차 정보" />
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="space-y-4">
              <InputBox
                label="대리점"
                value={loadingInfos[0]?.loadPlace || ""}
                onChange={e => updateLoadingInfo(1, "loadPlace", e.target.value)}
              />
            </div>
            <div className="space-y-4">
              <InputBox
                required
                label="상차지"
                value={loadingInfos[0]?.unloadPlace || ""}
                onChange={e => updateLoadingInfo(1, "unloadPlace", e.target.value)}
              />
            </div>
            <div className="space-y-4">
              <InputBox
                label="수량"
                value={loadingQty}
                onChange={e => setLoadingQty(e.target.value)}
              />
            </div>
          </div>

          <ModalHeader
            title="하차 정보 (최대 3개)"
            rightComponent={
              <Button
                variant="add"
                className="mb-[6px]"
                onClick={addUnloadingInfo}
                disabled={unloadingInfos.length >= 3}
              >
                추가
              </Button>
            }
          />
          <div className="space-y-3 mb-4">
            {unloadingInfos.map((info, index) => (
              <div key={info.id} className="flex items-end gap-3">
                <div className="flex-1">
                  <InputBox
                    required={index === 0}
                    label={`하차지 ${index + 1}`}
                    value={info.unloadPlace}
                    onChange={e => updateUnloadingInfo(info.id, "unloadPlace", e.target.value)}
                  />
                </div>
                <div className="flex-1">
                  <InputBox
                    label={`지역 ${index + 1}`}
                    value={info.region}
                    onChange={e => updateUnloadingInfo(info.id, "region", e.target.value)}
                  />
                </div>
                <div className="w-[120px]">
                  <InputBox
                    label={`수량 ${index + 1}`}
                    value={info.quantity}
                    onChange={e => updateUnloadingInfo(info.id, "quantity", e.target.value)}
                  />
                </div>
                {index > 0 ? (
                  <button
                    type="button"
                    onClick={() => removeUnloadingInfo(info.id)}
                    className="mb-1 p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="삭제"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                ) : (
                  <div className="w-9" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* 모달 푸터 */}
      <div className="flex items-center gap-3 mt-6">
        <div className="flex justify-start w-full">
          <Button variant="white" onClick={onClose} className=" py-[10px]">
            취소
          </Button>
        </div>
        <div className="flex justify-end w-full gap-3">
          <Button
            variant="primaryReverse"
            onClick={() => handleSave(false)}
            className="py-[10px]"
            disabled={
              insertAndUpdateMutation.isPending ||
              (mode === "edit" && (initialData?.saleProcessed || initialData?.dispatched))
            }
          >
            {insertAndUpdateMutation.isPending ? "저장 중..." : "저장"}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleSave(true)}
            className="py-[10px]"
            disabled={
              insertAndUpdateMutation.isPending ||
              (mode === "edit" && (initialData?.saleProcessed || initialData?.dispatched))
            }
          >
            {insertAndUpdateMutation.isPending ? "저장 중..." : "연속저장"}
          </Button>
        </div>
      </div>

      {/* 검색 모달 */}

      {/* 매출처 검색 모달 (실제 API) */}
      <CompanySearchModal
        isOpen={isCompanySearchOpen}
        onClose={() => setIsCompanySearchOpen(false)}
        onSelect={(company: StCompanyCode) => {
          if (company.stCompanyName) {
            setOrderLocation(company.stCompanyName); // 화면에 이름 표시
          }
          if (company.stCompanyCode) {
            setOrderLocationCode(String(company.stCompanyCode)); // DB 저장용 코드
          }
          setIsCompanySearchOpen(false);
        }}
      />

      {/* 품목 검색 모달 (실제 API) */}
      <ItemCodeSearchModal
        isOpen={isItemCodeSearchOpen}
        onClose={() => setIsItemCodeSearchOpen(false)}
        onSelect={(item: ItemCode) => {
          if (item.stItemName) {
            setOrderItem(item.stItemName); // 화면에 품목명 표시
          }
          if (item.stItemCode) {
            setOrderItemCode(item.stItemCode); // DB 저장용 코드
          }
          setIsItemCodeSearchOpen(false);
        }}
      />

      {/* 차량번호 검색 모달 */}
      <CarNumberSearchModal
        isOpen={isCarNumberSearchOpen}
        onClose={() => setIsCarNumberSearchOpen(false)}
        companyGb={""} // 회사 코드 전달
        onSelect={(vehicle: VehicleCode) => {
          if (vehicle.stVehicleNumber1) {
            setVehicleNumber(vehicle.stVehicleNumber1); // 화면에 차량번호 표시
          }
          if (vehicle.stVehicleCode) {
            setVehicleCode(vehicle.stVehicleCode); // DB 저장용 코드
          }
          if (vehicle.stVehicleDrivercode || vehicle.stDriverCode) {
            setDriverCode(vehicle.stVehicleDrivercode || vehicle.stDriverCode); // 기사 코드 자동 입력
          }
          if (vehicle.driverName || vehicle.stVehicleDrivername) {
            setDriverName(vehicle.driverName || vehicle.stVehicleDrivername || ""); // 기사명 자동 입력
          }
          if (vehicle.mobileTel || vehicle.stVehicleDrivertel) {
            setDriverTel(vehicle.mobileTel || vehicle.stVehicleDrivertel || ""); // 전화번호 자동 입력
          }
          setIsCarNumberSearchOpen(false);
        }}
      />

      {/* 기사명 검색 모달 */}
      <DriverSearchModal
        isOpen={isDriverSearchOpen}
        onClose={() => setIsDriverSearchOpen(false)}
        onSelect={(driver: DriverCode) => {
          if (driver.stDriverName) {
            setDriverName(driver.stDriverName); // 화면에 기사명 표시
          }
          if (driver.stDriverCode) {
            setDriverCode(driver.stDriverCode); // DB 저장용 코드
          }
          if (driver.stDriverMobileTel) {
            setDriverTel(driver.stDriverMobileTel); // 전화번호도 자동 입력
          }
          setIsDriverSearchOpen(false);
        }}
      />

      {/* 배차사원 검색 모달 */}
      <EmployeeSearchModal
        isOpen={isEmployeeSearchOpen}
        onClose={() => setIsEmployeeSearchOpen(false)}
        onSelect={(employee: EmployeeCode) => {
          if (employee.name) {
            setDispatchEmployee(employee.name); // 화면에 배차사원명 표시
          }
          if (employee.personCode) {
            setDispatchEmployeeCode(employee.personCode); // DB 저장용 사원코드
          }
          setIsEmployeeSearchOpen(false);
        }}
      />

      {/* 단가코드 검색 모달 */}
      <PriceCodeSearchModal
        isOpen={isPriceCodeSearchOpen}
        onClose={() => setIsPriceCodeSearchOpen(false)}
        onSelect={priceInfo => {
          // 매출단가 설정
          setSaleUnitPrice(priceInfo.unitPrice);
          // 선택된 단가코드 정보 저장
          setSelectedPriceInfo({
            priceCodeId: priceInfo.priceCodeId,
            companyName: priceInfo.companyName,
            region: priceInfo.region,
            dist: priceInfo.dist,
            weight: priceInfo.weight,
          });
          setIsPriceCodeSearchOpen(false);
        }}
      />
    </ModalLayout>
  );
}
